// AiAssetImportModal — 技術資産 / IT資産 / 備品 共通のAIインポートモーダル (2026-06).
// 在庫AIインポート(v2)と同じUX。バックエンドは /api/asset-ai-import/{module}/*。
import { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, UploadSimple, FileXls, DownloadSimple, Trash, ArrowCounterClockwise,
  WarningCircle, CheckCircle, XCircle, ArrowLeft,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/asset-ai-import`;
const ACCEPT = '.xlsx,.csv,.png,.jpg,.jpeg,.webp,.pdf';
const NONE = '__none__';

export const MODULE_CONFIG = {
  eng: {
    title: '技術資産AIインポート', tpl: '技術資産',
    accent: 'bg-teal-700 hover:bg-teal-800', gradient: 'from-teal-500 to-emerald-600',
    hint: '例: 全ての資産を技術部の保管品として扱ってください',
    grid: [
      { f: 'name', l: '品名＊', w: 'w-40' }, { f: 'qty', l: '数量', w: 'w-14' },
      { f: 'uom', l: '単位', w: 'w-16' }, { f: 'sku', l: '品番', w: 'w-28' },
      { f: 'sku_rule', l: '品番ルール', w: 'w-40' },
      { f: 'model_no', l: '型式・仕様', w: 'w-32' }, { f: 'maker', l: 'メーカー', w: 'w-28' },
      { f: 'material', l: '材質', w: 'w-24' }, { f: 'order_category', l: '受注分類', w: 'w-36' },
      { f: 'location', l: '保管場所', w: 'w-28' }, { f: 'status_ja', l: 'ステータス', w: 'w-24' },
    ],
  },
  it: {
    title: 'IT資産AIインポート', tpl: 'IT資産',
    accent: 'bg-teal-700 hover:bg-teal-800', gradient: 'from-blue-500 to-indigo-600',
    hint: '例: 全てのPCはセンスビー製造部の共用資産として扱ってください',
    grid: [
      { f: 'name', l: '品名＊', w: 'w-40' }, { f: 'asset_tag', l: '資産番号', w: 'w-28' },
      { f: 'tag_rule', l: '資産番号ルール', w: 'w-40' },
      { f: 'category', l: 'カテゴリ', w: 'w-28' }, { f: 'status_ja', l: 'ステータス', w: 'w-24' },
      { f: 'maker', l: 'メーカー', w: 'w-28' }, { f: 'model', l: '型番', w: 'w-28' },
      { f: 'serial', l: 'シリアル', w: 'w-28' }, { f: 'location', l: '設置場所', w: 'w-28' },
      { f: 'department', l: '割当先部門', w: 'w-32' }, { f: 'ownership_ja', l: '所有区分', w: 'w-24' },
      { f: 'cost', l: '金額', w: 'w-20' },
    ],
  },
  bihin: {
    title: '備品AIインポート', tpl: '備品',
    accent: 'bg-teal-700 hover:bg-teal-800', gradient: 'from-green-500 to-emerald-600',
    hint: '例: 全ての備品は2F会議室の保管品として扱ってください',
    grid: [
      { f: 'name', l: '品名＊', w: 'w-40' }, { f: 'kind_ja', l: '種別', w: 'w-28' },
      { f: 'asset_no', l: '品番/資産番号', w: 'w-28' },
      { f: 'tag_rule', l: '資産番号ルール', w: 'w-40' },
      { f: 'department', l: '所属部門', w: 'w-32' },
      { f: 'location', l: '保管場所', w: 'w-28' }, { f: 'status_ja', l: 'ステータス', w: 'w-24' },
      { f: 'category', l: '分類', w: 'w-24' }, { f: 'maker', l: 'メーカー', w: 'w-28' },
      { f: 'model_no', l: '型式', w: 'w-28' }, { f: 'price', l: '価格', w: 'w-20' },
      { f: 'next_inspection_date', l: '次回点検日', w: 'w-28' },
    ],
  },
};

const STATUS_META = {
  ok:      { label: 'OK',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle },
  warning: { label: '警告', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: WarningCircle },
  error:   { label: 'エラー', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.warning;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold shrink-0 ${m.cls}`}>
      <m.Icon size={11} weight="fill" />{m.label}
    </span>
  );
}

export default function AiAssetImportModal({ module, open, onClose, onImported }) {
  const cfg = MODULE_CONFIG[module];
  const BASE = `${API}/${module}`;
  const [step, setStep] = useState('input');
  const [file, setFile] = useState(null);
  const [hint, setHint] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('new_only');
  const [filter, setFilter] = useState('all');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setStep('input'); setFile(null); setHint(''); setData(null); setRows([]);
    setMode('new_only'); setFilter('all'); setResult(null); setRollingBack(false);
  };

  const editOptions = data?.edit_options || {};

  const downloadTemplate = async (sample) => {
    try {
      const res = await axios.get(`${BASE}/template`, { params: { sample: sample ? 1 : 0 }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = sample ? `${cfg.tpl}テンプレート_サンプル付き.xlsx` : `${cfg.tpl}テンプレート.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) { toast.error('テンプレートのダウンロードに失敗しました'); }
  };

  const analyze = async () => {
    if (!file) { toast.error('ファイルをアップロードしてください'); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (hint) fd.append('hint', hint);
      const res = (await axios.post(`${BASE}/analyze`, fd)).data;
      if (!res.rows?.length) { toast.error('インポート可能な行がありません。'); return; }
      setData(res);
      setRows(res.rows.map((r, i) => ({ ...r, _key: `r-${i}` })));
      (res.header_warnings || []).forEach((w) => toast.warning(w));
      toast.success(`${res.summary.total}件を解析しました（OK ${res.summary.ok} / 警告 ${res.summary.warning} / エラー ${res.summary.error}）`);
      setStep('review');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ファイルを読み取れませんでした。形式を確認してください。');
    } finally { setAnalyzing(false); }
  };

  const setRow = (key, patch) => setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) => setRows((prev) => prev.filter((r) => r._key !== key));

  const filtered = useMemo(() => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)), [rows, filter]);
  const importable = useMemo(() => rows.filter((r) => r.status !== 'error' && r.action !== 'skip'), [rows]);
  const counts = useMemo(() => ({
    ok: rows.filter((r) => r.status === 'ok').length,
    warning: rows.filter((r) => r.status === 'warning').length,
    error: rows.filter((r) => r.status === 'error').length,
  }), [rows]);

  const downloadErrorCsv = (errRows) => {
    const src = errRows || rows.filter((r) => r.status === 'error').map((r) => ({ row_no: r.row_no, name: r.name, error: (r.messages || []).join(' / ') }));
    if (!src.length) { toast.info('エラー行はありません'); return; }
    const csv = ['行,名前,エラー内容', ...src.map((r) => `${r.row_no},"${(r.name || '').replace(/"/g, '""')}","${(r.error || '').replace(/"/g, '""')}"`)].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${module}_import_errors.csv`;
    a.click();
  };

  const commit = async () => {
    if (!importable.length) { toast.error('インポート可能な行がありません。'); return; }
    if (mode === 'allow_update' && !window.confirm('既存更新モードです。重複する既存データが上書き更新されます。よろしいですか？')) return;
    if (!window.confirm(`${importable.length}件を登録します。よろしいですか？（エラー行は除外されます）`)) return;
    setCommitting(true);
    try {
      const res = (await axios.post(`${BASE}/commit`, {
        rows: rows.map(({ _key, ...r }) => r), mode,
        file_name: data?.file_name || null, source: 'file', hint: hint || null,
      })).data;
      setResult(res);
      setStep('result');
      if (res.created > 0 || res.updated > 0) {
        toast.success(`登録 ${res.created}件 / 更新 ${res.updated}件`);
        onImported?.();
      }
      if (res.errors?.length) toast.error('一部の行のみ登録されました。エラー内容を確認してください。');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '登録に失敗しました');
    } finally { setCommitting(false); }
  };

  const rollbackBatch = async () => {
    const batchId = result?.batch?.id;
    if (!batchId) return;
    if (!window.confirm(`AIインポートで登録した ${result.batch.item_count}件 を取消します。よろしいですか？`)) return;
    setRollingBack(true);
    try {
      const res = (await axios.post(`${BASE}/batches/${batchId}/undo`)).data;
      setResult((prev) => ({ ...prev, batch: res.batch }));
      toast.success(`${res.summary.archived}件を取消しました${res.summary.skipped ? `（${res.summary.skipped}件はスキップ）` : ''}`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ロールバックに失敗しました');
    } finally { setRollingBack(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-7xl max-h-[92vh] overflow-hidden flex flex-col" data-testid={`asset-ai-import-dialog-${module}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${cfg.gradient} text-white flex items-center justify-center`}>
              <Sparkle size={15} weight="fill" />
            </span>
            {cfg.title}
          </DialogTitle>
          <DialogDescription>
            Excel / CSV / 画像 / PDF ファイルをアップロードしてください。AIが内容を解析し、登録前にプレビューを表示します。
          </DialogDescription>
        </DialogHeader>

        {step === 'input' && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="asset-ai-import-input">
            <button type="button" data-testid="asset-ai-upload-zone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              className={`w-full rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                dragOver ? 'border-teal-400 bg-teal-50 dark:bg-teal-950/30' : 'border-zinc-300 dark:border-zinc-600 hover:border-teal-300 hover:bg-teal-50/40'
              }`}>
              <UploadSimple size={28} className="mx-auto text-teal-600" weight="bold" />
              <div className="mt-2 text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                ファイルをアップロード（Excel / CSV / 画像 / PDF）
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">.xlsx / .csv / .png / .jpg / .jpeg / .webp / .pdf · クリックまたはドラッグ&ドロップ</div>
              {file && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 text-xs font-semibold" data-testid="asset-ai-file-chip">
                  <FileXls size={14} weight="fill" />{file.name}
                  <span role="button" tabIndex={0} className="text-zinc-500 hover:text-rose-500 cursor-pointer"
                    onClick={(ev) => { ev.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                    onKeyDown={() => {}}>✕</span>
                </div>
              )}
            </button>
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="asset-ai-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />

            <div>
              <Label className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">追加指示（任意）</Label>
              <Input data-testid="asset-ai-hint" value={hint} onChange={(e) => setHint(e.target.value)}
                placeholder={cfg.hint} className="h-10 mt-1" />
            </div>

            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-3">
              <div className="text-xs font-bold text-zinc-600 dark:text-zinc-300 mb-2">テンプレート:</div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="asset-ai-template-blank"
                  onClick={() => downloadTemplate(false)}>
                  <DownloadSimple size={13} weight="bold" className="mr-1" />{cfg.tpl}テンプレートをダウンロード
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="asset-ai-template-sample"
                  onClick={() => downloadTemplate(true)}>
                  <DownloadSimple size={13} weight="bold" className="mr-1" />サンプル付き{cfg.tpl}テンプレートをダウンロード
                </Button>
              </div>
              <div className="mt-2 text-[11px] text-zinc-500">
                テンプレートは現在のマスターデータからドロップダウン付きで生成されます。
              </div>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="flex-1 overflow-hidden flex flex-col gap-3" data-testid="asset-ai-import-review">
            <div className="flex flex-wrap items-center gap-2">
              {['all', 'ok', 'warning', 'error'].map((k) => (
                <button key={k} type="button" data-testid={`asset-ai-filter-${k}`} onClick={() => setFilter(k)}
                  className={`px-2.5 py-1 rounded-full border text-[11px] font-bold transition-colors ${
                    filter === k ? 'bg-zinc-800 text-white border-zinc-800 dark:bg-zinc-100 dark:text-zinc-900' : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-300 dark:border-zinc-600'
                  }`}>
                  {k === 'all' ? `すべて ${rows.length}` : `${STATUS_META[k].label} ${counts[k]}`}
                </button>
              ))}
              <div className="flex-1" />
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="asset-ai-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_only">新規登録のみ（重複はスキップ）</SelectItem>
                  <SelectItem value="allow_update">既存更新を許可</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="asset-ai-error-csv"
                onClick={() => downloadErrorCsv()}>
                <DownloadSimple size={13} className="mr-1" />エラーCSV
              </Button>
            </div>
            {mode === 'allow_update' && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="asset-ai-update-warning">
                ⚠ 既存更新モード: 「既存を更新」を選択した行は、既存データの内容が上書き更新されます。
              </div>
            )}

            <div className="flex-1 overflow-auto border border-zinc-200 dark:border-zinc-700 rounded-lg">
              <table className="text-[11px] border-collapse min-w-max">
                <thead className="sticky top-0 bg-zinc-100 dark:bg-zinc-800 z-10">
                  <tr>
                    <th className="px-1.5 py-1.5 text-left font-bold text-zinc-600 dark:text-zinc-300 border-b border-zinc-200 dark:border-zinc-700">#</th>
                    <th className="px-1.5 py-1.5 text-left font-bold text-zinc-600 dark:text-zinc-300 border-b border-zinc-200 dark:border-zinc-700">状態</th>
                    {cfg.grid.map((c) => (
                      <th key={c.f} className="px-1.5 py-1.5 text-left font-bold text-zinc-600 dark:text-zinc-300 whitespace-nowrap border-b border-zinc-200 dark:border-zinc-700">{c.l}</th>
                    ))}
                    <th className="px-1.5 py-1.5 text-left font-bold text-zinc-600 dark:text-zinc-300 border-b border-zinc-200 dark:border-zinc-700">処理</th>
                    <th className="px-1.5 py-1.5 text-left font-bold text-zinc-600 dark:text-zinc-300 border-b border-zinc-200 dark:border-zinc-700">メッセージ</th>
                    <th className="border-b border-zinc-200 dark:border-zinc-700" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r._key} className="border-b border-zinc-100 dark:border-zinc-800 align-top" data-testid={`asset-ai-row-${r.row_no}`}>
                      <td className="px-1.5 py-1 text-zinc-400 tabular-nums">{r.row_no}</td>
                      <td className="px-1.5 py-1"><StatusBadge status={r.status} /></td>
                      {cfg.grid.map((c) => {
                        const opts = [...new Set(editOptions[c.f] || [])];
                        return (
                          <td key={c.f} className="px-1.5 py-1">
                            {opts.length > 0 ? (
                              <Select value={r[c.f] || NONE} onValueChange={(v) => setRow(r._key, { [c.f]: v === NONE ? '' : v })}>
                                <SelectTrigger className={`h-7 text-[11px] px-1.5 ${c.w}`} data-testid={`asset-ai-cell-${c.f}-${r.row_no}`}>
                                  <SelectValue placeholder="なし" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  <SelectItem value={NONE}>なし</SelectItem>
                                  {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                  {r[c.f] && !opts.includes(r[c.f]) && <SelectItem value={r[c.f]}>{r[c.f]}</SelectItem>}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input value={r[c.f] ?? ''} onChange={(e) => setRow(r._key, { [c.f]: e.target.value })}
                                className={`h-7 text-[11px] px-1.5 ${c.w}`} data-testid={`asset-ai-cell-${c.f}-${r.row_no}`} />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-1.5 py-1">
                        <Select value={r.action} onValueChange={(v) => setRow(r._key, { action: v })}>
                          <SelectTrigger className="h-7 w-28 text-[11px]" data-testid={`asset-ai-action-${r.row_no}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="create">新規登録</SelectItem>
                            {r.duplicate_of && mode === 'allow_update' && <SelectItem value="update">既存を更新</SelectItem>}
                            <SelectItem value="skip">スキップ</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-1.5 py-1 max-w-[240px]">
                        {(r.messages || []).map((msg, i) => (
                          <div key={i} className="text-[10px] text-zinc-500 leading-tight">· {msg}</div>
                        ))}
                        {r.duplicate_of && (
                          <div className="text-[10px] text-amber-600 leading-tight">既存: {r.duplicate_of.sku || ''} {r.duplicate_of.name}</div>
                        )}
                      </td>
                      <td className="px-1.5 py-1">
                        <button type="button" onClick={() => removeRow(r._key)} data-testid={`asset-ai-remove-${r.row_no}`}
                          className="w-6 h-6 rounded flex items-center justify-center text-zinc-400 hover:text-rose-600 hover:bg-rose-50">
                          <Trash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-zinc-500">エラー行は登録から自動的に除外されます。値を編集した場合、登録確定時にサーバー側で再検証されます。</div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="flex-1 overflow-y-auto space-y-4" data-testid="asset-ai-import-result">
            <div className="grid grid-cols-4 gap-3">
              {[['登録', result.created, 'text-emerald-600'], ['更新', result.updated, 'text-sky-600'],
                ['スキップ', result.skipped, 'text-zinc-500'], ['エラー', result.errors?.length || 0, 'text-rose-600']].map(([label, n, cls]) => (
                <div key={label} className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 text-center">
                  <div className={`text-2xl font-bold tabular-nums ${cls}`}>{n}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-rose-700">一部の行のみ登録されました。エラー内容を確認してください。</div>
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="asset-ai-result-error-csv"
                    onClick={() => downloadErrorCsv(result.errors)}>
                    <DownloadSimple size={12} className="mr-1" />エラーCSV
                  </Button>
                </div>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-[11px] text-rose-700">行{e.row_no} {e.name}: {e.error}</div>
                  ))}
                </div>
              </div>
            )}
            {result.batch && (
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 flex items-center justify-between">
                <div className="text-xs text-zinc-600 dark:text-zinc-300">
                  インポート履歴に記録しました（バッチID: <span className="font-mono">{result.batch.id?.slice(0, 8)}</span>）。
                  {result.batch.undo_status === 'undone' ? ' このバッチは取消済みです。' : ' 誤りがあれば一括取消できます。'}
                </div>
                {result.batch.undo_status !== 'undone' && (
                  <Button variant="outline" size="sm" className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={rollbackBatch} disabled={rollingBack} data-testid="asset-ai-rollback">
                    <ArrowCounterClockwise size={13} className="mr-1" />
                    {rollingBack ? '取消中...' : `一括取消（${result.batch.item_count}件）`}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-zinc-100 dark:border-zinc-800 pt-3">
          {step === 'input' && (
            <>
              <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="asset-ai-cancel">キャンセル</Button>
              <Button onClick={analyze} disabled={analyzing || !file} className={`${cfg.accent} text-white`} data-testid="asset-ai-analyze-btn">
                {analyzing ? 'AIが解析中...' : 'AIで解析 →'}
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="asset-ai-review-cancel">キャンセル</Button>
              <Button variant="outline" onClick={() => setStep('input')} data-testid="asset-ai-back">
                <ArrowLeft size={13} className="mr-1" />戻る
              </Button>
              <Button onClick={commit} disabled={committing || importable.length === 0}
                className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="asset-ai-commit-btn">
                {committing ? '登録中...' : `登録を確定（${importable.length}件）`}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={() => { reset(); onClose(); }} data-testid="asset-ai-close">閉じる</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
